import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { secureStorage } from '../lib/secureStorage';
import { AuthContextType, AuthUser, Clinic, User } from '../types';
import { safeConsole } from '../lib/piiProtection';
import { broadcastAuthEvent, onAuthEvent } from '../lib/authChannel';
import { useIdleLogout } from '../hooks/useIdleLogout';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Proactively refresh the session on a fixed cadence, comfortably under the 24h access-token TTL, so
// a still-active user is never bounced to /login. We no longer decode the JWT for its expiry — the
// token isn't in JS any more (it lives only in the httpOnly cookie); a periodic cookie-based refresh
// is enough and far simpler. authModule.refresh() rotates the cookie server-side.
const REFRESH_INTERVAL_MS = 20 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Starts (or restarts) the periodic silent session refresh. Idempotent. */
  const startTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    refreshTimerRef.current = setInterval(() => {
      // Fire-and-forget: a failed refresh surfaces as a 401 on the next real request, which
      // fetchApi already handles (silent retry, then redirect-to-login).
      api.auth.refresh().catch(() => {});
    }, REFRESH_INTERVAL_MS);
  }, []);

  const stopTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, []);

  /**
   * Establishes React auth state from the shared, httpOnly `auth_token` cookie by asking the server
   * who it is (`GET /auth/me`) — the browser sends the cookie automatically. This is the single source
   * of truth for "am I logged in", so a second tab/window inherits an existing login instead of
   * showing /login, and a cross-tab `login` event re-runs it. Returns whether a session was found.
   */
  const bootstrapFromCookie = useCallback(async (): Promise<boolean> => {
    try {
      const response = await api.auth.getCurrentUser();

      // Backend returns {data: {user, clinic}} which fetchApi wraps in {data: ...}.
      // On 401 (no/expired cookie) fetchApi already tried one silent refresh, then returns
      // { error }, so response.data is undefined and we fall through to the logged-out state.
      const responseData = response.data?.data || response.data;

      if (responseData && responseData.user) {
        setUser({ id: responseData.user.id, email: responseData.user.name });
        setUserData(responseData.user);
        setClinic(responseData.clinic);
        secureStorage.setAuthToken(); // mark this tab's session (no token is stored)
        startTokenRefresh();
        return true;
      }
      setUser(null);
      setUserData(null);
      setClinic(null);
      secureStorage.removeAuthToken();
      return false;
    } catch {
      safeConsole.error('Auth initialization error');
      setUser(null);
      setUserData(null);
      setClinic(null);
      secureStorage.removeAuthToken();
      return false;
    }
  }, [startTokenRefresh]);

  /** Clears this tab's auth state and per-tab caches WITHOUT calling the logout API. Shared by the
   *  local signOut and by a `logout` broadcast from another tab (which already hit the API). */
  const clearLocalAuthState = useCallback(() => {
    stopTokenRefresh();
    sessionStorage.removeItem('lastActivity');
    localStorage.removeItem('lastUsedPharmacyId');
    localStorage.removeItem('favoritePharmacies');
    localStorage.removeItem('favoriteDiagnoses');
    sessionStorage.removeItem('recentPatients');
    localStorage.removeItem('recentPatients');
    setUser(null);
    setUserData(null);
    setClinic(null);
    secureStorage.removeAuthToken();
  }, [stopTokenRefresh]);

  useEffect(() => {
    bootstrapFromCookie().finally(() => setLoading(false));
  }, [bootstrapFromCookie]);

  // Cross-tab sync: converge every tab of this browser on login/logout instead of waiting for the
  // next navigation. On a remote logout, clear locally (route guards then redirect to /login); on a
  // remote login, re-bootstrap from the now-present cookie.
  useEffect(() => {
    return onAuthEvent((event) => {
      if (event === 'logout') {
        clearLocalAuthState();
      } else if (event === 'login') {
        bootstrapFromCookie();
      }
    });
  }, [bootstrapFromCookie, clearLocalAuthState]);

  /** Shared by signIn and completeMfaLogin — both land here once a real session is established. */
  const applyLoginResponse = (response: { data?: any; error?: string }) => {
    const responseData = response.data?.data || response.data;
    const outerData = response.data as any;

    if (!responseData || !responseData.user) {
      safeConsole.error('Response data is missing user');
      throw new Error('Invalid response from server: missing user data');
    }

    // Reset session activity on login to prevent immediate logout
    sessionStorage.setItem('lastActivity', String(Date.now()));

    setUser({ id: responseData.user.id, email: responseData.user.name });
    setUserData(responseData.user);
    setClinic(responseData.clinic);

    // Multi-branch: store the default active clinic from login response.
    // TenantContext.tsx picks this up on mount.
    if (outerData?.activeClinicId) {
      secureStorage.setActiveClinicId(outerData.activeClinicId);
    }

    // The session now lives in the httpOnly cookie the login response set; mark this tab and start
    // the periodic silent refresh.
    secureStorage.setAuthToken();
    startTokenRefresh();

    // Tell other tabs of this browser to pick up the new session from the cookie.
    broadcastAuthEvent('login');
  };

  const signIn = async (email: string, password: string) => {
    try {
      const response = await api.auth.login(email, password);

      if (response.error) {
        throw new Error(response.error);
      }

      const responseData = (response.data as any)?.data || response.data;

      // MFA-enabled account: password was correct, but no session exists yet — the caller
      // (Login.tsx) must collect a code and call completeMfaLogin with this token.
      if (responseData?.mfaRequired) {
        return { mfaRequired: true as const, mfaPendingToken: responseData.mfaPendingToken as string };
      }

      applyLoginResponse(response);
      return { mfaRequired: false as const };
    } catch (error) {
      safeConsole.error('Sign-in error');
      throw error;
    }
  };

  const completeMfaLogin = async (mfaPendingToken: string, code?: string, backupCode?: string) => {
    try {
      const response = await api.mfa.verify(mfaPendingToken, code, backupCode);
      if (response.error) {
        throw new Error(response.error);
      }
      applyLoginResponse(response);
    } catch (error) {
      safeConsole.error('MFA verification error');
      throw error;
    }
  };

  const signOut = useCallback(async () => {
    try {
      // Clear this tab's state + per-tab PII caches (DPDP Act 2023 § 8: no retention beyond session)
      // and stop the refresh timer. The shared httpOnly cookie is cleared server-side by the API call.
      clearLocalAuthState();

      // Tell every other tab of this browser to drop to the login screen too.
      broadcastAuthEvent('logout');

      // Call logout API non-blocking (don't wait for it or throw errors) — this revokes the token
      // server-side and clears the shared cookie. Non-blocking so it can't interfere with a login
      // attempt right after an auto-logout.
      api.auth.logout().catch(() => {
        safeConsole.log('Logout API call completed (error ignored)');
      });
    } catch {
      safeConsole.error('Sign out error');
      clearLocalAuthState();
    }
  }, [clearLocalAuthState]);

  // Universal inactivity logout (all roles), active only while logged in. See useIdleLogout.
  useIdleLogout(!!user, signOut);

  const refreshAuth = async () => {
    try {
      const response = await api.auth.getCurrentUser();
      
      // Backend returns {data: {user, clinic}} which fetchApi wraps in {data: ...}
      const responseData = response.data?.data || response.data;
      
      if (responseData && responseData.user) {
        setUser({ id: responseData.user.id, email: responseData.user.name });
        setUserData(responseData.user);
        setClinic(responseData.clinic);
      } else {
        throw new Error('Invalid response from server: missing user data');
      }
    } catch (error) {
      safeConsole.error('Refresh auth error');
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userData,
    clinic,
    loading,
    isSuperAdmin: userData?.role === 'super_admin',
    signIn,
    completeMfaLogin,
    signOut,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
